// Offline-resilience helpers for the customer app.
//
// 1. Menu cache (stale-while-revalidate): the last successfully fetched
//    menu is kept in localStorage per restaurant. On the next visit — or
//    when the network is down — the cached menu renders instantly while
//    a fresh copy is fetched in the background.
// 2. newClientOrderId(): idempotency key for order POSTs so a retry
//    after a network blip can never create a duplicate order.
// 3. useOnline(): hook tracking navigator.onLine for the banner.

import { useEffect, useState } from "react";

const CACHE_PREFIX = "odra_menu_cache_v1:";
// Cache entries older than this are ignored (menu/prices may have changed
// too much to be trustworthy).
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h

export const readMenuCache = (restaurantId) => {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + restaurantId);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry || !Array.isArray(entry.menu)) return null;
    if (Date.now() - (entry.ts || 0) > MAX_AGE_MS) return null;
    return entry; // { ts, menu, chefPicks, topSellers }
  } catch {
    return null;
  }
};

export const writeMenuCache = (restaurantId, { menu, chefPicks, topSellers }) => {
  try {
    localStorage.setItem(
      CACHE_PREFIX + restaurantId,
      JSON.stringify({ ts: Date.now(), menu, chefPicks, topSellers })
    );
  } catch {
    // localStorage full or unavailable (private mode) — caching is
    // best-effort, never break the page over it.
  }
};

export const newClientOrderId = () => {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  // Older WebViews: timestamp + randomness is unique enough for a
  // per-order key.
  return `ord-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const useOnline = () => {
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);
  return online;
};
